<script>
  // Searchable replacement for a native <select>, styled with DaisyUI (the native
  // popup can't be). Two modes:
  //   static — pass `options` ([{ value, label }] or plain strings); filtered client-side,
  //            the search box only appears past `searchThreshold` options
  //   async  — pass `source` (an AdminSite::OptionsProp source key); options are
  //            searched + paginated server-side, with a Load-more tail. The label for an
  //            already-saved `value` hydrates itself via an `ids` lookup when unknown.
  // `sourceParams` are extra query params (e.g. { area_id }) — changing them refetches.
  import Icon from './icon.svelte'
  import Button from './button.svelte'
  import { loadSelectOptions } from '../../lib/shared_props.js'

  let {
    value = $bindable(''),
    options = [],
    source = null,
    sourceParams = {},
    selected = null, // { value, label } — known label for the initial async value
    placeholder = 'Select…',
    clearable = false,
    size = 'md', // 'md' | 'sm'
    width = 'w-full',
    disabled = false,
    error = false,
    id = undefined,
    ariaLabel = null,
    searchThreshold = 8,
    onchange = null
  } = $props()

  let open = $state(false)
  let query = $state('')
  let highlighted = $state(0)
  let items = $state([])
  let pagy = $state(null)
  let loading = $state(false)
  let loadFailed = $state(false)
  let labelCache = $state({})
  let root = $state(null)
  let trigger = $state(null)

  let requestId = 0
  let fetchedKey = null
  const hydrating = new Set()

  const normalized = $derived(
    options.map((option) =>
      typeof option === 'object' && option !== null ? option : { value: option, label: option }
    )
  )
  const staticFiltered = $derived(
    normalized.filter((option) =>
      String(option.label).toLowerCase().includes(query.trim().toLowerCase())
    )
  )
  const list = $derived(source ? items : staticFiltered)
  const showSearch = $derived(!!source || normalized.length > searchThreshold)
  const hasMore = $derived(!!source && !!pagy && pagy.pages > pagy.page)
  const hasValue = $derived(value !== '' && value != null)
  const clearRow = $derived(clearable && hasValue)
  const paramsKey = $derived(JSON.stringify(sourceParams ?? {}))

  const selectedLabel = $derived.by(() => {
    if (!hasValue) return null
    const fromStatic = normalized.find((option) => option.value === value)
    if (fromStatic) return fromStatic.label
    if (selected && selected.value === value) return selected.label
    if (labelCache[value] != null) return labelCache[value]
    const fromItems = items.find((option) => option.value === value)
    return fromItems ? fromItems.label : null
  })
  // A list with an explicit blank choice ("None") names it instead of the placeholder.
  const blankLabel = $derived(normalized.find((option) => option.value === '')?.label)
  const display = $derived(selectedLabel ?? (hasValue ? '…' : (blankLabel ?? placeholder ?? 'Select…')))

  // Hydrate the label for a saved value the open pages haven't produced yet. No
  // sourceParams here: a saved value may fall outside the current filters (e.g. a
  // place from another college town) and still needs its label.
  $effect(() => {
    if (!source || !hasValue || selectedLabel != null || hydrating.has(value)) return
    const lookup = value
    hydrating.add(lookup)
    loadSelectOptions(source, { ids: lookup })
      .then((data) => {
        const option = data.options.find((candidate) => candidate.value === lookup)
        if (option) labelCache[option.value] = option.label
      })
      .catch(() => {})
  })

  const load = (pageNumber, append = false) => {
    const rid = ++requestId
    loading = true
    loadFailed = false
    loadSelectOptions(source, { q: query, page: pageNumber, params: sourceParams ?? {} })
      .then((data) => {
        if (rid !== requestId) return
        // Dedupe on append: a record created between page fetches shifts pagination
        // and could repeat an option (duplicate {#each} keys crash).
        items = append
          ? [...items, ...data.options.filter((option) => !items.some((item) => item.value === option.value))]
          : data.options
        pagy = data.pagy
        loading = false
        highlighted = Math.min(highlighted, itemCount() - 1)
      })
      .catch(() => {
        if (rid !== requestId) return
        loading = false
        loadFailed = true
      })
  }

  // Fetch page 1 whenever the panel is open and the query or sourceParams changed
  // (debounced after the initial fetch); reopening always refreshes.
  $effect(() => {
    if (!source || !open) return
    const key = `${query.trim()}|${paramsKey}`
    if (key === fetchedKey) return
    const timeoutId = setTimeout(
      () => {
        fetchedKey = key
        load(1)
      },
      fetchedKey === null ? 0 : 250
    )
    return () => clearTimeout(timeoutId)
  })

  const itemCount = () => list.length + (clearRow ? 1 : 0)

  const openPanel = () => {
    if (disabled) return
    open = true
    query = ''
    highlighted = 0
    if (source) fetchedKey = null
  }

  const close = (focusTrigger = false) => {
    open = false
    if (focusTrigger) trigger?.focus()
  }

  const choose = (option) => {
    value = option ? option.value : ''
    if (option) labelCache[option.value] = option.label
    close(true)
    onchange?.(option ? option.value : '', option ?? null)
  }

  const onkeydown = (event) => {
    if (!open) {
      if (['ArrowDown', 'Enter', ' '].includes(event.key)) {
        event.preventDefault()
        openPanel()
      }
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      close(true)
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      highlighted = Math.min(highlighted + 1, itemCount() - 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      highlighted = Math.max(highlighted - 1, 0)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (clearRow && highlighted === 0) choose(null)
      else {
        const option = list[clearRow ? highlighted - 1 : highlighted]
        if (option) choose(option)
      }
    } else if (event.key === 'Tab') {
      close()
    }
  }

  // Keep the keyboard-highlighted row visible while arrowing through a long list.
  $effect(() => {
    if (!open || highlighted < 0) return
    root?.querySelector('[data-highlighted="true"]')?.scrollIntoView({ block: 'nearest' })
  })

  const focusOnMount = (node) => node.focus()
</script>

<svelte:document
  onpointerdown={(event) => {
    if (open && root && !root.contains(event.target)) close()
  }}
/>

<!-- svelte-ignore a11y_no_static_element_interactions (keyboard events bubble here from the trigger + search input) -->
<div class="dropdown {open ? 'dropdown-open' : ''} {width}" bind:this={root} {onkeydown}>
  <button
    type="button"
    bind:this={trigger}
    {id}
    {disabled}
    class="select {size === 'sm' ? 'select-sm' : ''} w-full items-center justify-start text-left gap-2 {error ? 'select-error' : ''}"
    role="combobox"
    aria-expanded={open}
    aria-haspopup="listbox"
    aria-controls={open ? `${id ?? 'combobox'}-listbox` : undefined}
    aria-label={ariaLabel ?? undefined}
    onclick={() => (open ? close() : openPanel())}
  >
    <span class="truncate {hasValue ? '' : 'text-base-content/40'}">{display}</span>
  </button>

  {#if open}
    <div class="dropdown-content bg-base-100 border border-base-300 rounded-box shadow-lg z-30 mt-1 w-full min-w-48 p-2 flex flex-col gap-2">
      {#if showSearch}
        <label class="input input-sm w-full">
          <Icon name="search" size="sm" class="text-base-content/40" />
          <input
            type="search"
            bind:value={query}
            use:focusOnMount
            oninput={() => (highlighted = 0)}
            placeholder="Search…"
            aria-label="Search options"
            class="[&::-webkit-search-cancel-button]:hidden"
          />
          {#if query}
            <button
              type="button"
              class="text-base-content/40 hover:text-base-content cursor-pointer"
              aria-label="Clear search"
              onclick={() => {
                query = ''
                highlighted = 0
              }}
            >
              <Icon name="close" size="xs" />
            </button>
          {/if}
        </label>
      {/if}

      <ul
        class="menu menu-sm w-full max-h-64 overflow-y-auto flex-nowrap p-0"
        role="listbox"
        id={`${id ?? 'combobox'}-listbox`}
      >
        {#if clearRow}
          <li>
            <button
              type="button"
              role="option"
              aria-selected="false"
              class={highlighted === 0 ? 'bg-primary/10 text-primary' : 'text-base-content/60'}
              data-highlighted={highlighted === 0}
              onclick={() => choose(null)}
            >
              {placeholder ?? 'Clear'}
            </button>
          </li>
        {/if}
        {#each list as option, index (option.value)}
          {@const rowIndex = clearRow ? index + 1 : index}
          <li>
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              class={`${highlighted === rowIndex ? 'bg-primary/10 text-primary' : ''} ${option.value === value ? 'text-primary font-medium' : ''}`}
              data-highlighted={highlighted === rowIndex}
              onclick={() => choose(option)}
            >
              <span class="truncate">{option.label}</span>
              {#if option.value === value}
                <Icon name="check" size="xs" class="ml-auto shrink-0 text-primary" />
              {/if}
            </button>
          </li>
        {/each}
        {#if !loading && list.length === 0}
          <li class="px-3 py-2 text-sm text-base-content/60">
            {loadFailed ? 'Failed to load options.' : 'No matches.'}
          </li>
        {/if}
      </ul>

      {#if loading}
        <div class="flex justify-center py-1"><span class="loading loading-spinner loading-xs"></span></div>
      {:else if hasMore}
        <Button variant="ghost" size="xs" onclick={() => load(pagy.page + 1, true)}>
          Load more ({items.length} of {pagy.count})
        </Button>
      {/if}
    </div>
  {/if}
</div>
